import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function VatConfigSection({ customer, vat, setVat, vatCheck, setVatCheck, vatChecking, setVatChecking, onSaved }) {
  return (
    <section data-testid="vat-config-section">
      <h4 className="text-sm font-bold tracking-tight mb-2 flex items-center gap-2">
        € Umsatzsteuer (Kunden-Override)
      </h4>
      <p className="text-xs text-zinc-500 mb-2">
        Wenn aktiv: Rechnungen an diesen Kunden werden ohne USt ausgestellt. Der Text zur Rechtsgrundlage erscheint auf der Rechnung als Pflichthinweis.
      </p>
      <div className="space-y-2">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={vat.exempt}
            onChange={(e) => setVat({ ...vat, exempt: e.target.checked, reverse_charge: e.target.checked ? false : vat.reverse_charge })}
            className="w-5 h-5"
            data-testid="vat-exempt-cb"
          />
          <span className="text-sm">Kunde ist von der Umsatzsteuer befreit (z. B. Kleinunternehmer § 19 UStG)</span>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={vat.reverse_charge}
            onChange={(e) => setVat({ ...vat, reverse_charge: e.target.checked, exempt: e.target.checked ? false : vat.exempt })}
            className="w-5 h-5"
            data-testid="vat-reverse-charge-cb"
          />
          <span className="text-sm">Reverse Charge (EU-B2B, § 13b UStG) — USt-Schuldnerschaft beim Empfänger</span>
        </label>
      </div>
      {vat.exempt && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs">Rechtsgrundlage / Steuerartikel</Label>
            <Input
              type="text"
              className="h-9 rounded-none"
              value={vat.article}
              onChange={(e) => setVat({ ...vat, article: e.target.value })}
              placeholder="z. B. § 19 UStG (Kleinunternehmer)"
              data-testid="vat-article-input"
            />
          </div>
          <div>
            <Label className="text-xs">Hinweistext für Rechnung</Label>
            <Input
              type="text"
              className="h-9 rounded-none"
              value={vat.note}
              onChange={(e) => setVat({ ...vat, note: e.target.value })}
              placeholder="Gemäß Kleinunternehmerregelung wird keine USt ausgewiesen."
              data-testid="vat-note-input"
            />
          </div>
        </div>
      )}
      {vat.reverse_charge && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs">USt-IdNr. des Kunden (Pflichtangabe)</Label>
            <div className="flex gap-2 items-stretch">
              <Input
                type="text"
                className="h-9 rounded-none font-mono flex-1"
                value={vat.vat_id}
                onChange={(e) => { setVat({ ...vat, vat_id: e.target.value.toUpperCase() }); setVatCheck(null); }}
                placeholder="z. B. ATU12345678"
                data-testid="vat-id-input"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!vat.vat_id) return;
                  setVatChecking(true); setVatCheck(null);
                  try {
                    const r = await api.post(`/customers/${customer.customer_id}/vat/validate`, { vat_id: vat.vat_id });
                    setVatCheck({ ...r.data.result, persisted: true });
                    if (onSaved) onSaved({ ...customer, vat_config: r.data.vat_config });
                  } catch (e) {
                    setVatCheck({ valid: false, error: e.response?.data?.detail || e.message });
                  } finally { setVatChecking(false); }
                }}
                disabled={!vat.vat_id || vatChecking}
                className="px-3 h-9 rounded-none text-xs font-semibold bg-black text-white hover:bg-zinc-800 disabled:opacity-50 whitespace-nowrap"
                data-testid="vat-validate-btn"
              >
                {vatChecking ? "Prüfe…" : "VIES prüfen"}
              </button>
            </div>
            {vatCheck && (
              <div
                className={`mt-1.5 text-[11px] p-2 rounded ${
                  vatCheck.valid === true ? "bg-emerald-50 text-emerald-800 border border-emerald-200" :
                  vatCheck.valid === false ? "bg-red-50 text-red-800 border border-red-200" :
                  "bg-amber-50 text-amber-800 border border-amber-200"
                }`}
                data-testid="vat-validate-result"
              >
                {vatCheck.valid === true && (
                  <><strong>✓ Gültig (VIES)</strong>
                    {vatCheck.name && vatCheck.name !== "---" && <div>Firma: <span className="font-mono">{vatCheck.name}</span></div>}
                    {vatCheck.address && vatCheck.address !== "---" && <div className="text-[10px] mt-0.5 opacity-80">{vatCheck.address}</div>}
                  </>
                )}
                {vatCheck.valid === false && (
                  <><strong>✗ Ungültig</strong>{vatCheck.error && <span> — {vatCheck.error}</span>}</>
                )}
                {vatCheck.valid === null && (
                  <><strong>? VIES nicht erreichbar</strong>{vatCheck.error && <span> — {vatCheck.error}</span>}</>
                )}
                {vatCheck.checked_at && (
                  <div className="text-[10px] mt-1 opacity-70">
                    {vatCheck.persisted ? "🔒 gespeichert · " : ""}
                    Letzte Prüfung: {new Date(vatCheck.checked_at).toLocaleString("de-DE")}
                    {vatCheck.checked_by && ` durch ${vatCheck.checked_by}`}
                  </div>
                )}
              </div>
            )}
            {!vat.vat_id && (
              <div className="text-[10px] text-amber-700 mt-1">⚠ Ohne USt-IdNr. darf keine Reverse-Charge-Rechnung ausgestellt werden.</div>
            )}
          </div>
          <div>
            <Label className="text-xs">Zusatz-Hinweistext (optional)</Label>
            <Input
              type="text"
              className="h-9 rounded-none"
              value={vat.note}
              onChange={(e) => setVat({ ...vat, note: e.target.value })}
              placeholder="Standard: Steuerschuldnerschaft des Leistungsempfängers gem. § 13b UStG."
              data-testid="vat-note-rc-input"
            />
          </div>
        </div>
      )}
    </section>

  );
}
